import { BACKEND_API_BASE_URL } from '../config';

/**
 * Reports API Service
 * Handles communication with the reports API
 */

class ReportsAPI {
  constructor() {
    this.baseURL = BACKEND_API_BASE_URL;
  }

  /**
   * Get authorization headers with provided token
   */
  getAuthHeaders(authToken) {
    if (!authToken) {
      throw new Error('Authentication token is required');
    }
    
    return {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Make API request with error handling
   */
  async makeRequest(endpoint, authToken, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: this.getAuthHeaders(authToken),
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Reports API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * Get automatic report for school (last 1 day)
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @returns {Promise<Object>} Automatic report data
   */
  async getAutomaticReport(authToken, schoolId) {
    return await this.makeRequest(`/api/reports/school/${schoolId}/automatic`, authToken);
  }

  /**
   * Request manual report generation
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Manual report generation result
   */
  async requestManualReport(authToken, schoolId, startDate, endDate) {
    return await this.makeRequest(`/api/reports/school/${schoolId}/manual`, authToken, {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate })
    });
  }

  /**
   * Check cooldown status for manual report generation
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @returns {Promise<Object>} Cooldown status
   */
  async checkCooldown(authToken, schoolId) {
    return await this.makeRequest(`/api/reports/school/${schoolId}/cooldown`, authToken);
  }

  /**
   * Get report generation history for a school
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @param {number} limit - Number of records to return (default: 10)
   * @returns {Promise<Object>} Report generation history
   */
  async getReportHistory(authToken, schoolId, limit = 10) {
    const params = new URLSearchParams({ limit: limit.toString() });
    return await this.makeRequest(`/api/reports/school/${schoolId}/history?${params}`, authToken);
  }

  /**
   * Get completed reports for a school
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @param {number} limit - Number of reports to fetch
   * @param {string} status - Report status filter ('COMPLETED', 'PENDING', 'FAILED')
   * @returns {Promise<Object>} Completed reports data
   */
  async getCompletedReports(authToken, schoolId, limit = 10, status = 'COMPLETED') {
    const params = new URLSearchParams({
      limit: limit.toString(),
      status
    });

    return await this.makeRequest(`/api/reports/school/${schoolId}/completed?${params}`, authToken);
  }

  /**
   * Get pending reports for a school
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @param {number} limit - Number of reports to fetch
   * @returns {Promise<Object>} Pending reports data
   */
  async getPendingReports(authToken, schoolId, limit = 10) {
    const params = new URLSearchParams({
      limit: limit.toString()
    });

    return await this.makeRequest(`/api/reports/school/${schoolId}/pending?${params}`, authToken);
  }

  /**
   * Get all reports (completed and pending) for a school
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School unique ID
   * @param {number} limit - Number of reports to fetch per status
   * @returns {Promise<Object>} All reports data
   */
  async getAllReports(authToken, schoolId, limit = 10) {
    const [completed, pending] = await Promise.all([
      this.getCompletedReports(authToken, schoolId, limit, 'COMPLETED'),
      this.getPendingReports(authToken, schoolId, limit)
    ]);

    return {
      success: true,
      completed: completed.reports || [],
      pending: pending.reports || [],
      totalCompleted: completed.count || 0,
      totalPending: pending.count || 0,
      config: completed.config || pending.config
    };
  }

  /**
   * Get report generation configuration
   * @param {string} authToken - JWT authentication token
   * @returns {Promise<Object>} Configuration settings
   */
  async getReportConfig(authToken) {
    return await this.makeRequest('/api/reports/config', authToken);
  }
}

// Create singleton instance
const reportsAPI = new ReportsAPI();

export default reportsAPI;
